// BlogPage.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    { id: "all", name: "All" },
    { id: "insights", name: "Insights" },
    { id: "market-trends", name: "Market Trends" },
    { id: "thought-leadership", name: "Thought Leadership" },
    { id: "case-studies", name: "Case Studies" },
    { id: "news", name: "News" },
  ];

  const blogPosts = [
    {
      id: 1,
      slug: "future-of-private-equity-trends-shaping-2024",
      title: "The Future of Private Equity: Trends Shaping 2024",
      excerpt:
        "As we navigate through 2024, private equity firms are adapting to new market realities. From increased regulatory scrutiny to the rise of technology-driven due diligence, here's what leading firms need to know.",
      category: "market-trends",
      date: "March 15, 2024",
      readTime: "8 min read",
      author: "Sarah Johnson",
      authorTitle: "Managing Partner",
      image:
        "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&auto=format",
      featured: true,
    },
    {
      id: 2,
      slug: "esg-integration-beyond-compliance-to-value-creation",
      title: "ESG Integration: Beyond Compliance to Value Creation",
      excerpt:
        "Environmental, Social, and Governance factors are no longer just compliance metrics. Forward-thinking firms are discovering how ESG integration drives operational efficiency and unlocks hidden value.",
      category: "insights",
      date: "March 10, 2024",
      readTime: "6 min read",
      author: "Michael Chen",
      authorTitle: "ESG Director",
      image:
        "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format",
      featured: false,
    },
    {
      id: 3,
      slug: "navigating-exits-in-a-volatile-market",
      title: "Navigating Exits in a Volatile Market",
      excerpt:
        "Exit planning has become increasingly complex. Our experts share strategies for maximizing value during uncertain times, including preparation tactics and timing considerations.",
      category: "thought-leadership",
      date: "March 5, 2024",
      readTime: "10 min read",
      author: "David Williams",
      authorTitle: "Head of M&A",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format",
      featured: true,
    },
    {
      id: 4,
      slug: "rise-of-ai-in-financial-due-diligence",
      title: "The Rise of AI in Financial Due Diligence",
      excerpt:
        "Artificial Intelligence is transforming how we approach due diligence. From automated document review to predictive analytics, discover how technology is reshaping the landscape.",
      category: "insights",
      date: "February 28, 2024",
      readTime: "5 min read",
      author: "Emily Rodriguez",
      authorTitle: "Technology Practice Lead",
      image:
        "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format",
      featured: false,
    },
    {
      id: 5,
      slug: "cross-border-ma-overcoming-cultural-and-regulatory-hurdles",
      title: "Cross-Border M&A: Overcoming Cultural and Regulatory Hurdles",
      excerpt:
        "International transactions present unique challenges. This comprehensive guide explores best practices for navigating cross-border deals across different jurisdictions.",
      category: "case-studies",
      date: "February 20, 2024",
      readTime: "12 min read",
      author: "James Okonkwo",
      authorTitle: "International Markets Director",
      image:
        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format",
      featured: false,
    },
    {
      id: 6,
      slug: "morphis-announces-strategic-partnership-with-global-tech-leaders",
      title: "Morphis Announces Strategic Partnership with Global Tech Leaders",
      excerpt:
        "We're excited to announce a new collaboration that will enhance our technology advisory capabilities and bring greater value to our portfolio companies.",
      category: "news",
      date: "February 15, 2024",
      readTime: "3 min read",
      author: "Communications Team",
      authorTitle: "",
      image:
        "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&auto=format",
      featured: false,
    },
    {
      id: 7,
      slug: "portfolio-company-optimization-the-first-100-days",
      title: "Portfolio Company Optimization: The First 100 Days",
      excerpt:
        "The initial months after acquisition are critical. Learn proven frameworks for driving operational improvements and accelerating growth in newly acquired companies.",
      category: "thought-leadership",
      date: "February 10, 2024",
      readTime: "9 min read",
      author: "Robert Taylor",
      authorTitle: "Operations Partner",
      image:
        "https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&auto=format",
      featured: false,
    },
    {
      id: 8,
      slug: "valuation-trends-in-the-current-economic-climate",
      title: "Valuation Trends in the Current Economic Climate",
      excerpt:
        "Understanding current valuation methodologies and market multiples is essential. Our valuation experts share insights on what's driving prices across sectors.",
      category: "market-trends",
      date: "February 5, 2024",
      readTime: "7 min read",
      author: "Patricia Lee",
      authorTitle: "Valuation Practice Lead",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format",
      featured: false,
    },
  ];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory =
      activeCategory === "all" || post.category === activeCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter((post) => post.featured);

  return (
    <>
      <Breadcrumb />
      <div className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="museum-wrap">
            <div className="blog-hero-content">
              <span className="section-label">Insights & Perspectives</span>
              <h1>
                The Morphis <em>Blog</em>
              </h1>
              <p className="blog-hero-subtitle">
                Expert insights, market analysis, and thought leadership from
                our team of advisors. Stay informed on the latest trends shaping
                capital markets and private equity.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <section className="blog-featured">
            <div className="museum-wrap">
              <div className="featured-header">
                <span className="featured-label">Featured</span>
                <h2>Editor's Picks</h2>
              </div>
              <div className="featured-grid">
                {featuredPosts.map((post) => (
                  <Link
                    to={`/blogs/${post.slug}`}
                    key={post.id}
                    className="featured-card"
                  >
                    <div className="featured-card-image">
                      <img src={post.image} alt={post.title} />
                      <span className="featured-badge">Featured</span>
                    </div>
                    <div className="featured-card-content">
                      <div className="post-meta">
                        <span className="post-category">
                          {categories.find((c) => c.id === post.category)?.name}
                        </span>
                        <span className="post-date">{post.date}</span>
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="post-author">
                        <span className="author-name">{post.author}</span>
                        {post.authorTitle && (
                          <span className="author-title">
                            {post.authorTitle}
                          </span>
                        )}
                      </div>
                      <div className="read-more">
                        Read Article <i className="arrow-icon"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Posts Section */}
        <section className="blog-all-posts">
          <div className="museum-wrap">
            <div className="blog-header">
              <h2>Latest Insights</h2>
              <div className="blog-filters">
                <div className="category-filters">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      className={`category-btn ${activeCategory === category.id ? "active" : ""}`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <i className="search-icon">🔍</i>
                </div>
              </div>
            </div>

            <div className="posts-grid">
              {filteredPosts.map((post) => (
                <Link
                  to={`/blogs/${post.slug}`}
                  key={post.id}
                  className="post-card"
                >
                  <div className="post-card-image">
                    <img src={post.image} alt={post.title} />
                    <span className="post-category-badge">
                      {categories.find((c) => c.id === post.category)?.name}
                    </span>
                  </div>
                  <div className="post-card-content">
                    <div className="post-meta">
                      <span className="post-date">{post.date}</span>
                      <span className="post-read-time">{post.readTime}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="post-footer">
                      <div className="post-author-mini">
                        <span>{post.author}</span>
                      </div>
                      <span className="post-link">Read More </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="no-results">
                <p>No articles found matching your criteria.</p>
                <button
                  onClick={() => {
                    setActiveCategory("all");
                    setSearchTerm("");
                  }}
                  className="reset-btn"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="blog-newsletter">
          <div className="museum-wrap">
            <div className="newsletter-content">
              <span className="section-label">Stay Connected</span>
              <h2>Subscribe to Our Newsletter</h2>
              <p>
                Get the latest insights and market trends delivered directly to
                your inbox.
              </p>
              <form className="newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="newsletter-input"
                />
                <button type="submit" className="newsletter-btn">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>

        <style>{`
          .blog-page {
            background: var(--bg-main, #fffdf6);
            min-height: 100vh;
          }

          /* Hero Section */
          .blog-hero {
            padding-top: 88px;
            background: var(--bg-main, #fffdf6);
            border-bottom: 1px solid var(--border-light, #e6e0d4);
          }

          .blog-hero-content {
            padding: 60px 0 80px;
            max-width: 800px;
          }

          .blog-hero-content h1 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(2.5rem, 5vw, 4rem);
            line-height: 1.1;
            margin-bottom: 1.5rem;
            color: var(--color-dark, #1a1a1a);
          }

          .blog-hero-content h1 em {
            font-style: italic;
            color: var(--color-orange, #e87722);
          }

          .blog-hero-subtitle {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: var(--fs-sub-line, 1.15rem);
            color: var(--text-muted, #6b7280);
            line-height: 1.6;
          }

          /* Featured Section */
          .blog-featured {
            padding: 80px 0;
            background: var(--bg-main, #fffdf6);
          }

          .featured-header {
            text-align: center;
            margin-bottom: 50px;
          }

          .featured-label {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: var(--fs-eyebrow, 0.82rem);
            font-weight: 400;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: var(--color-orange, #e87722);
            display: inline-block;
            margin-bottom: 12px;
          }

          .featured-header h2 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(1.75rem, 4vw, 2.5rem);
            color: var(--color-dark, #1a1a1a);
          }

          .featured-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 32px;
          }

          .featured-card {
            text-decoration: none;
            color: inherit;
            border: 1px solid var(--border-light, #e6e0d4);
            overflow: hidden;
            transition: all 0.3s ease;
            display: block;
          }

          .featured-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
          }

          .featured-card-image {
            position: relative;
            height: 280px;
            overflow: hidden;
          }

          .featured-card-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
          }

          .featured-card:hover .featured-card-image img {
            transform: scale(1.05);
          }

          .featured-badge {
            position: absolute;
            top: 16px;
            right: 16px;
            background: var(--color-orange, #e87722);
            color: #fff;
            padding: 4px 12px;
            font-size: 11px;
            font-weight: 400;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }

          .featured-card-content {
            padding: 28px;
          }

          .featured-card-content h3 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: 1.5rem;
            margin: 12px 0;
            color: var(--color-dark, #1a1a1a);
          }

          .featured-card-content p {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 0.95rem;
            line-height: 1.6;
            color: var(--text-sub, #2d2d2d);
            margin-bottom: 20px;
          }

          .post-meta {
            display: flex;
            gap: 16px;
            margin-bottom: 12px;
          }

          .post-category {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 400;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color-orange, #e87722);
          }

          .post-date {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            color: var(--text-muted, #6b7280);
          }

          .post-author {
            margin: 16px 0;
            padding-top: 12px;
            border-top: 1px solid var(--border-light, #e6e0d4);
          }

          .author-name {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 13px;
            font-weight: 400;
            color: var(--color-dark, #1a1a1a);
            display: block;
          }

          .author-title {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            color: var(--text-muted, #6b7280);
          }

          .read-more {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 400;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color-orange, #e87722);
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }

          .arrow-icon {
            transition: transform 0.2s ease;
          }

          .featured-card:hover .arrow-icon {
            transform: translateX(6px);
          }

          /* All Posts Section */
          .blog-all-posts {
            padding: 80px 0;
            background: var(--bg-main, #fffdf6);
            border-top: 1px solid var(--border-light, #e6e0d4);
          }

          .blog-header {
            margin-bottom: 50px;
          }

          .blog-header h2 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(1.5rem, 3vw, 2rem);
            margin-bottom: 24px;
            color: var(--color-dark, #1a1a1a);
          }

          .blog-filters {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 20px;
          }

          .category-filters {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
          }

          .category-btn {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 13px;
            font-weight: 400;
            padding: 8px 18px;
            background: transparent;
            border: 1px solid var(--border-light, #e6e0d4);
            color: var(--text-sub, #2d2d2d);
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .category-btn:hover,
          .category-btn.active {
            background: var(--color-orange, #e87722);
            border-color: var(--color-orange, #e87722);
            color: #fff;
          }

          .search-wrapper {
            position: relative;
          }

          .search-input {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 14px;
            padding: 10px 16px;
            padding-right: 40px;
            border: 1px solid var(--border-light, #e6e0d4);
            background: transparent;
            width: 260px;
            outline: none;
            transition: border-color 0.2s;
          }

          .search-input:focus {
            border-color: var(--color-orange, #e87722);
          }

          .search-icon {
            position: absolute;
            right: 14px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 16px;
            opacity: 0.5;
          }

          .posts-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 32px;
          }

          .post-card {
            text-decoration: none;
            color: inherit;
            border: 1px solid var(--border-light, #e6e0d4);
            overflow: hidden;
            transition: all 0.3s ease;
            display: block;
          }

          .post-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
          }

          .post-card-image {
            position: relative;
            height: 200px;
            overflow: hidden;
          }

          .post-card-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
          }

          .post-card:hover .post-card-image img {
            transform: scale(1.05);
          }

          .post-category-badge {
            position: absolute;
            bottom: 12px;
            left: 12px;
            background: var(--color-orange, #e87722);
            color: #fff;
            padding: 4px 10px;
            font-size: 10px;
            font-weight: 400;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }

          .post-card-content {
            padding: 20px;
          }

          .post-read-time {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            color: var(--text-muted, #6b7280);
          }

          .post-card-content h3 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: 1.2rem;
            margin: 12px 0;
            line-height: 1.4;
            color: var(--color-dark, #1a1a1a);
          }

          .post-card-content p {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 0.85rem;
            line-height: 1.6;
            color: var(--text-sub, #2d2d2d);
            margin-bottom: 16px;
          }

          .post-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid var(--border-light, #e6e0d4);
          }

          .post-author-mini {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 400;
            color: var(--text-muted, #6b7280);
          }

          .post-link {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 400;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: var(--color-orange, #e87722);
          }

          .no-results {
            text-align: center;
            padding: 60px 20px;
          }

          .no-results p {
            font-family: var(--font-body, Arial, sans-serif);
            color: var(--text-muted, #6b7280);
            margin-bottom: 20px;
          }

          .reset-btn {
            font-family: var(--font-body, Arial, sans-serif);
            padding: 10px 24px;
            background: var(--color-orange, #e87722);
            color: #fff;
            border: none;
            cursor: pointer;
          }

          .blog-newsletter {
            padding: 80px 0;
            background:
              linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.7)),
              url("https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&auto=format")
                center/cover no-repeat;
            border-top: 1px solid var(--border-light, #e6e0d4);
            position: relative;
          }

          .newsletter-content {
            text-align: center;
            max-width: 600px;
            margin: 0 auto;
          }

          .newsletter-content h2 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(1.5rem, 4vw, 2rem);
            margin-bottom: 16px;
            color: #ffffff;
          }

          .newsletter-content p {
            font-family: var(--font-body, Arial, sans-serif);
            color: #ffffff;
            margin-bottom: 32px;
          }

          .newsletter-form {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .newsletter-input {
            flex: 1;
            min-width: 280px;
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 14px;
            padding: 14px 20px;
            border: 1px solid var(--border-light, #e6e0d4);
            background: #fff;
            outline: none;
          }

          .newsletter-input:focus {
            border-color: var(--color-orange, #e87722);
          }

          .newsletter-btn {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 400;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            padding: 14px 32px;
            background: var(--color-dark, #1a1a1a);
            border: 1px solid #ffffff;
            color: #fff;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .newsletter-btn:hover {
            background: var(--color-orange, #e87722);
            border-color: var(--color-orange, #e87722);
          }

          /* Responsive */
          @media (max-width: 992px) {
            .featured-grid {
              grid-template-columns: 1fr;
            }

            .posts-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (max-width: 768px) {
            .blog-hero {
              padding-top: 68px;
            }

            .blog-hero-content {
              padding: 40px 0 60px;
            }

            .blog-filters {
              flex-direction: column;
              align-items: stretch;
            }

            .category-filters {
              justify-content: center;
            }

            .search-wrapper {
              width: 100%;
            }

            .search-input {
              width: 100%;
            }

            .posts-grid {
              grid-template-columns: 1fr;
            }

            .newsletter-form {
              flex-direction: column;
            }

            .newsletter-input {
              width: 100%;
            }
          }

          @media (max-width: 480px) {
            .category-filters {
              gap: 8px;
            }

            .category-btn {
              font-size: 11px;
              padding: 6px 14px;
            }

            .featured-card-content {
              padding: 20px;
            }

            .featured-card-content h3 {
              font-size: 1.2rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default BlogPage;
